import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, Calendar, Rss } from "lucide-react";
import Nav from "@/components/Nav";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/posts`)
      .then((r) => setPosts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="blog-list-page" className="min-h-screen bg-[var(--bg)] text-[var(--text-1)]">
      <Seo
        title="Insights · KS DevArc Blog"
        description="Notes from the KS DevArc studio on building, shipping, design systems, and running a small studio in 2026."
        url={typeof window !== "undefined" ? window.location.href : ""}
      />
      <Nav />
      <section className="relative pt-44 pb-20">
        <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />
        <div className="relative mx-auto max-w-5xl px-5 sm:px-8">
          <div className="flex items-center justify-between gap-6 mb-6">
            <div className="flex items-center gap-3">
              <span className="w-2 h-2 bg-[var(--lime)] rounded-full" />
              <span className="overline">— Insights</span>
            </div>
            <a
              data-testid="blog-rss-link"
              href={`${API}/blog/rss`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-3 py-2 border border-[var(--border-dim)] hover:border-[var(--vermilion)] hover:text-[var(--vermilion)] rounded-full font-mono-ui text-[10px] tracking-[0.22em] uppercase transition-colors"
            >
              <Rss className="w-3 h-3" /> RSS
            </a>
          </div>
          <h1 className="font-display font-black text-5xl md:text-7xl tracking-tight leading-[0.95]">
            Notes from the<br />
            <span className="italic font-normal text-[var(--text-2)]">studio</span> on{" "}
            <span className="text-[var(--vermilion)]">craft.</span>
          </h1>
          <p className="mt-6 text-[var(--text-2)] max-w-xl">
            Thoughts on building, shipping, design systems, and running a small
            studio in 2026.
          </p>
        </div>
      </section>

      <section className="pb-32">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          {loading && (
            <div className="font-mono-ui text-[11px] tracking-[0.22em] uppercase text-[var(--text-3)]">
              Loading posts...
            </div>
          )}

          {!loading && posts.length === 0 && (
            <div data-testid="blog-empty" className="border border-[var(--border-dim)] rounded-sm bg-[var(--bg-2)] p-10 text-center">
              <div className="font-display text-xl">No posts published yet</div>
              <div className="overline mt-3">Come back soon for new insights.</div>
            </div>
          )}

          <div className="space-y-px bg-[var(--border-dim)] border border-[var(--border-dim)] rounded-sm overflow-hidden">
            {posts.map((p) => (
              <Link
                key={p.id}
                to={`/blog/${p.slug}`}
                data-testid={`blog-list-item-${p.slug}`}
                className="group block bg-[var(--bg)] hover:bg-[var(--bg-2)] px-6 md:px-10 py-8 transition-colors"
              >
                <div className="grid grid-cols-12 gap-6 items-start">
                  <div className="col-span-12 md:col-span-3 flex items-center gap-2 font-mono-ui text-[11px] tracking-[0.22em] uppercase text-[var(--text-3)]">
                    <Calendar className="w-3 h-3" />
                    {new Date(p.created_at).toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "short",
                      day: "2-digit",
                    })}
                  </div>
                  <div className="col-span-12 md:col-span-8">
                    <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight group-hover:text-[var(--vermilion)] transition-colors">
                      {p.title}
                    </h2>
                    {p.excerpt && (
                      <p className="mt-3 text-[var(--text-2)] max-w-2xl">{p.excerpt}</p>
                    )}
                    {p.tags?.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {p.tags.map((t) => (
                          <span key={t} className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--text-2)] border border-[var(--border-dim)] px-2 py-1 rounded-full">
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="hidden md:block col-span-1 text-right">
                    <ArrowUpRight className="w-5 h-5 text-[var(--text-3)] group-hover:text-[var(--lime)] group-hover:rotate-45 transition-all duration-300 inline-block" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
